import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  styled,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Security,
  SupportAgent,
  AttachMoney,
  Schedule,
  PersonalVideo,
} from '@mui/icons-material';
import Footer from '../../components/layout/Footer';
import Header from '../../components/layout/Header';
import { useTranslation } from 'react-i18next';

const HeroSection = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg, #8B1538 0%, #A85590 25%, #6B4C93 75%, #4A7C59 100%)',
  color: '#ffffff',
  padding: theme.spacing(12, 0),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'linear-gradient(45deg, rgba(139, 21, 56, 0.1) 0%, rgba(168, 85, 144, 0.1) 50%, rgba(74, 124, 89, 0.1) 100%)',
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    zIndex: 2,
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: '#ffffff',
}));

const FeatureSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: '#f8f9fa',
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '3.5rem',
  marginBottom: theme.spacing(3),
  fontFamily: '"Playfair Display", "Georgia", serif',
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  lineHeight: 1.6,
  maxWidth: '800px',
  margin: '0 auto',
  opacity: 0.95,
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.2rem',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#A85590',
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontFamily: '"Playfair Display", "Georgia", serif',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
}));

const StoryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: '0 8px 32px rgba(168, 85, 144, 0.1)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(168, 85, 144, 0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 48px rgba(168, 85, 144, 0.2)',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  border: '1px solid rgba(168, 85, 144, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(168, 85, 144, 0.15)',
    borderColor: '#A85590',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(168, 85, 144, 0.1)',
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s ease-in-out',
  '& .MuiSvgIcon-root': {
    color: '#A85590',
    fontSize: '2.5rem',
  },
  '.MuiCard-root:hover &': {
    backgroundColor: '#A85590',
    '& .MuiSvgIcon-root': {
      color: '#ffffff',
    },
  },
}));

const QuoteBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(168, 85, 144, 0.05)',
  border: '1px solid rgba(168, 85, 144, 0.2)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(4, 0),
  position: 'relative',
  paddingLeft: theme.spacing(6),
  '&::before': {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    fontSize: '3rem',
    color: '#A85590',
    opacity: 0.4,
    fontFamily: 'serif',
    lineHeight: 1,
  },
}));

const AboutUsPage = () => {
  const { t } = useTranslation();
  const features = [
    {
      icon: <AttachMoney />,
      title: t('about.features.transparency.title'),
      description: t('about.features.transparency.description'),
    },
    {
      icon: <SupportAgent />,
      title: t('about.features.responsive.title'),
      description: t('about.features.responsive.description'),
    },
    {
      icon: <PersonalVideo />,
      title: t('about.features.attention.title'),
      description: t('about.features.attention.title'),
    },
    {
      icon: <Schedule />,
      title: t('about.features.schedule.title'),
      description: t('about.features.schedule.description'),
    },
  ];

  return (
    <Box>
      <Header />
      <HeroSection>
        <Container maxWidth="lg">
          <HeroTitle variant="h1" as="h1">
            {t('about.title')}
          </HeroTitle>
          <HeroSubtitle variant="h5" as="p">
            {t('about.heroText')}
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <SectionTitle variant="h2" sx={{ textAlign: 'left', mb: 4 }}>
                {t('about.story.title')}
              </SectionTitle>
              <Typography
                variant="h6"
                sx={{ mb: 3, color: '#555', lineHeight: 1.7 }}
              >
                {t('about.story.description')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: '#666', lineHeight: 1.8 }}
              >
                {t('about.story.body1')}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: '#666', lineHeight: 1.8 }}
              >
                {t('about.story.body2')}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </ContentSection>

      <FeatureSection>
        <Container maxWidth="lg">
          <SectionTitle variant="h2">{t('about.bodyTitle')}</SectionTitle>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <FeatureCard>
                  <IconWrapper>{feature.icon}</IconWrapper>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: '#333', fontWeight: 600 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', lineHeight: 1.6 }}
                  >
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </FeatureSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  bgcolor: '#A85590',
                  color: 'white',
                }}
              >
                <Security sx={{ fontSize: '3rem', mb: 2 }} />
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  {t('about.mission.title')}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {t('about.mission.description')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 4, border: '2px solid #A85590' }}>
                <TrendingUp
                  sx={{ fontSize: '3rem', mb: 2, color: '#A85590' }}
                />
                <Typography
                  variant="h4"
                  sx={{ mb: 3, fontWeight: 600, color: '#A85590' }}
                >
                  {t('about.vision.title')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ lineHeight: 1.7, color: '#666' }}
                >
                  {t('about.vision.description')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  bgcolor: '#A85590',
                  color: 'white',
                }}
              >
                <Security sx={{ fontSize: '3rem', mb: 2 }} />
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  {t('services.values.core.title')}
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {t('services.values.core.description')}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </ContentSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <QuoteBox>
                <Typography
                  variant="h6"
                  sx={{
                    fontStyle: 'italic',
                    color: '#A85590',
                    lineHeight: 1.7,
                  }}
                >
                  &quot;{t('about.story.quote')}&quot;
                </Typography>
              </QuoteBox>
            </Grid>

            <Grid item xs={12} lg={6}>
              <StoryCard>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: '#A85590',
                        fontSize: '3rem',
                      }}
                    >
                      A&E
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ color: '#A85590', fontWeight: 600 }}
                    >
                      {t('about.story.subtext')}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: '#666', mt: 1 }}
                    >
                      {t('about.story.slogan')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 3 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <People
                          sx={{ color: '#A85590', fontSize: '2rem', mb: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: '#A85590' }}>
                          1000+
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {t('about.story.client')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <TrendingUp
                          sx={{ color: '#A85590', fontSize: '2rem', mb: 1 }}
                        />
                        <Typography variant="h6" sx={{ color: '#A85590' }}>
                          95%
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {t('about.story.satisfy')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StoryCard>
            </Grid>
          </Grid>
        </Container>
      </ContentSection>

      <Box sx={{ bgcolor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{ mb: 3, color: '#A85590', fontWeight: 700 }}
            >
              {t('about.footer.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, color: '#666', lineHeight: 1.6 }}
            >
              {t('about.footer.description')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Card sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#A85590', mb: 1 }}>
                  📞 Call Us
                </Typography>
                <Typography variant="body1">
                  {t('about.footer.call')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  832-831-3907
                </Typography>
              </Card>
              <Card sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#A85590', mb: 1 }}>
                  📧 {t('contactForm.mail')}
                </Typography>
                <a
                  href="mailto:info@aefinancialservicesllc.com"
                  style={{ textDecoration: 'none', color: '#666' }}
                >
                  <Typography variant="body1">
                    eMail: info@aefinancialservicesllc.com
                  </Typography>
                </a>
              </Card>
              <Card sx={{ p: 2, minWidth: 200, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#A85590', mb: 1 }}>
                  📅 Schedule
                </Typography>
                <Typography variant="body1">
                  {t('about.footer.schedule')}
                </Typography>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default AboutUsPage;
